import { useState, useEffect, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

/* ============================================================
   VERATRACK — Production Tracking Demo
   Fast Bay Solutions LLC · Modular Home Manufacturing
   Demo build: in-memory data, simulated auth, no backend.
   ============================================================ */

const FONTS = `
@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700&family=IBM+Plex+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;600;700&display=swap');
`;

const C = {
  bg: "#0b0f14",
  surface: "#121924",
  surface2: "#1a2432",
  line: "#243248",
  text: "#e8edf4",
  muted: "#8496ad",
  amber: "#f5a623",
  amberDim: "#3a2c10",
  green: "#4ade80",
  greenDim: "#0f2b1a",
  red: "#f87171",
  redDim: "#331416",
  blue: "#60a5fa",
  blueDim: "#12233d",
  cyan: "#22d3ee",
};

/* ---------------- seed data ---------------- */

const SEED_USERS = [
  { id: "u1", name: "Jairo Estrada", email: "jairo@fastbaysolutions.com", role: "admin", provider: "entra", active: true },
  { id: "u2", name: "Marisol Vega", email: "m.vega@verabuilt.com", role: "supervisor", provider: "entra", active: true },
  { id: "u3", name: "Dan Okafor", email: "d.okafor@verabuilt.com", role: "worker", provider: "standalone", active: true },
  { id: "u4", name: "Lena Fields", email: "l.fields@verabuilt.com", role: "worker", provider: "entra", active: true },
  { id: "u5", name: "Ray Soto", email: "r.soto@verabuilt.com", role: "worker", provider: "standalone", active: false },
];

const ROLES = ["admin", "supervisor", "worker"];

// Project Vera > Home Unit BLT004 > Station > Task > Subtask
const SEED_TREE = [
  {
    id: "p1", name: "Project Vera", client: "Vera Built Homes", status: "active",
    units: [
      {
        id: "hu1", code: "BLT004", model: "Ridgeline 2BR", status: "in_production",
        stations: [
          {
            id: "s1", name: "Flooring", seq: 1,
            tasks: [
              {
                id: "t1", name: "Subfloor Install",
                subtasks: [
                  { id: "st1", name: "Lay joist insulation", status: "approved", assignedTo: "u3", est: 2.0 },
                  { id: "st2", name: "Fasten OSB decking", status: "approved", assignedTo: "u3", est: 3.0 },
                ],
              },
              {
                id: "t2", name: "Finish Flooring",
                subtasks: [
                  { id: "st3", name: "LVP main living area", status: "submitted", assignedTo: "u4", est: 4.0 },
                  { id: "st4", name: "Transition strips + trim", status: "not_started", assignedTo: "u4", est: 1.5 },
                ],
              },
            ],
          },
          {
            id: "s2", name: "Plumbing", seq: 2,
            tasks: [
              {
                id: "t3", name: "Rough-In",
                subtasks: [
                  { id: "st5", name: "PEX supply runs", status: "in_progress", assignedTo: "u3", est: 3.5 },
                  { id: "st6", name: "DWV stack + venting", status: "not_started", assignedTo: "u3", est: 4.0 },
                ],
              },
              {
                id: "t4", name: "Fixture Set",
                subtasks: [
                  { id: "st7", name: "Set kitchen sink + faucet", status: "not_started", assignedTo: "u4", est: 1.5 },
                ],
              },
            ],
          },
          {
            id: "s3", name: "Electrical", seq: 3,
            tasks: [
              {
                id: "t5", name: "Rough Wiring",
                subtasks: [
                  { id: "st8", name: "Pull homerun circuits", status: "rejected", assignedTo: "u4", est: 3.0 },
                  { id: "st9", name: "Box + device rough-in", status: "not_started", assignedTo: "u4", est: 2.5 },
                ],
              },
              {
                id: "t6", name: "Panel & Trim",
                subtasks: [
                  { id: "st10", name: "Land breakers at panel", status: "not_started", assignedTo: "u3", est: 2.0 },
                ],
              },
            ],
          },
        ],
      },
      {
        id: "hu2", code: "BLT005", model: "Ridgeline 3BR", status: "queued",
        stations: [
          {
            id: "s4", name: "Flooring", seq: 1,
            tasks: [
              {
                id: "t7", name: "Subfloor Install",
                subtasks: [
                  { id: "st11", name: "Lay joist insulation", status: "not_started", assignedTo: "u4", est: 2.0 },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

// historical time entries (hours) — powers the dashboard
const SEED_TIME = [
  { id: "te1", subtaskId: "st1", userId: "u3", station: "Flooring", hours: 1.8, day: "Mon" },
  { id: "te2", subtaskId: "st2", userId: "u3", station: "Flooring", hours: 3.4, day: "Mon" },
  { id: "te3", subtaskId: "st3", userId: "u4", station: "Flooring", hours: 4.6, day: "Tue" },
  { id: "te4", subtaskId: "st5", userId: "u3", station: "Plumbing", hours: 2.1, day: "Tue" },
  { id: "te5", subtaskId: "st8", userId: "u4", station: "Electrical", hours: 3.9, day: "Wed" },
  { id: "te6", subtaskId: "st5", userId: "u3", station: "Plumbing", hours: 1.2, day: "Wed" },
];

const SEED_SIGNOFFS = [
  { id: "so1", subtaskId: "st1", step: "worker", by: "u3", action: "submitted", note: "", at: "Mon 14:12" },
  { id: "so2", subtaskId: "st1", step: "supervisor", by: "u2", action: "approved", note: "Verified spacing", at: "Mon 15:02" },
  { id: "so3", subtaskId: "st2", step: "worker", by: "u3", action: "submitted", note: "", at: "Mon 16:40" },
  { id: "so4", subtaskId: "st2", step: "supervisor", by: "u2", action: "approved", note: "", at: "Mon 17:05" },
  { id: "so5", subtaskId: "st3", step: "worker", by: "u4", action: "submitted", note: "Ready for QC", at: "Tue 13:30" },
  { id: "so6", subtaskId: "st8", step: "worker", by: "u4", action: "submitted", note: "", at: "Wed 11:20" },
  { id: "so7", subtaskId: "st8", step: "supervisor", by: "u2", action: "rejected", note: "Circuit 7 mislabeled — relabel and resubmit", at: "Wed 11:55" },
];

const STATUS_META = {
  not_started: { label: "Not Started", fg: C.muted, bg: "#1c2634" },
  in_progress: { label: "In Progress", fg: C.blue, bg: C.blueDim },
  submitted: { label: "Awaiting Sign-Off", fg: C.amber, bg: C.amberDim },
  approved: { label: "Approved", fg: C.green, bg: C.greenDim },
  rejected: { label: "Rejected", fg: C.red, bg: C.redDim },
};

const STATION_COLORS = { Flooring: C.amber, Plumbing: C.cyan, Electrical: C.blue };

/* ---------------- shared primitives ---------------- */

function Chip({ status }) {
  const m = STATUS_META[status] || STATUS_META.not_started;
  return (
    <span style={{ background: m.bg, color: m.fg }} className="px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
      {m.label}
    </span>
  );
}

function Card({ children, className = "", pad = true }) {
  return (
    <div className={`rounded-lg border ${pad ? "p-4" : ""} ${className}`} style={{ background: C.surface, borderColor: C.line }}>
      {children}
    </div>
  );
}

function Btn({ children, onClick, kind = "ghost", disabled, small, className = "" }) {
  const styles = {
    primary: { background: C.amber, color: "#171006", border: "1px solid " + C.amber },
    green: { background: C.green, color: "#06210f", border: "1px solid " + C.green },
    red: { background: "transparent", color: C.red, border: "1px solid " + C.red },
    ghost: { background: "transparent", color: C.text, border: "1px solid " + C.line },
  }[kind];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ ...styles, opacity: disabled ? 0.4 : 1, fontFamily: "'IBM Plex Sans',sans-serif" }}
      className={`${small ? "px-2.5 py-1 text-xs" : "px-3.5 py-2 text-sm"} rounded font-semibold transition-transform active:scale-95 ${className}`}
    >
      {children}
    </button>
  );
}

function SectionTitle({ kicker, title }) {
  return (
    <div className="mb-4">
      {kicker && <div className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: C.amber }}>{kicker}</div>}
      <div className="text-2xl font-semibold" style={{ fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: "0.01em" }}>{title}</div>
    </div>
  );
}

function fmtClock(sec) {
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = Math.floor(sec % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function findSubtask(tree, stId) {
  for (const p of tree) for (const u of p.units) for (const s of u.stations) for (const t of s.tasks)
    for (const st of t.subtasks) if (st.id === stId) return { p, u, s, t, st };
  return null;
}

/* ================= LOGIN ================= */

function LoginScreen({ users, settings, onLogin }) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");

  const domainOk = (em) => settings.domains.some((d) => em.toLowerCase().endsWith("@" + d.toLowerCase()));

  const tryStandalone = () => {
    setErr("");
    if (settings.authMode === "entra_only") return setErr("Standalone login is disabled. Use Microsoft Entra sign-in.");
    if (!domainOk(email)) return setErr("This email domain is not approved for access.");
    const u = users.find((x) => x.email.toLowerCase() === email.toLowerCase() && x.active);
    if (!u || !pw) return setErr("Invalid credentials or inactive account.");
    onLogin(u);
  };

  const tryEntra = (u) => {
    setErr("");
    if (settings.authMode === "standalone_only") return setErr("Entra SSO is disabled by the administrator.");
    if (!domainOk(u.email)) return setErr("This email domain is not approved for access.");
    if (!u.active) return setErr("Account is deactivated.");
    onLogin(u);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden" style={{ background: C.bg }}>
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `radial-gradient(ellipse 60% 40% at 70% 0%, ${C.amberDim}66, transparent), repeating-linear-gradient(0deg, transparent, transparent 39px, ${C.line}33 40px), repeating-linear-gradient(90deg, transparent, transparent 39px, ${C.line}33 40px)`,
      }} />
      <div className="w-full max-w-md relative">
        <div className="mb-8">
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] mb-1" style={{ color: C.amber }}>Fast Bay Solutions</div>
          <div className="text-5xl font-bold leading-none" style={{ fontFamily: "'Barlow Condensed',sans-serif", color: C.text }}>
            VERA<span style={{ color: C.amber }}>TRACK</span>
          </div>
          <div className="mt-1 text-sm" style={{ color: C.muted }}>Modular Home Production Tracking · Demo</div>
        </div>

        <Card className="mb-4">
          <button
            onClick={() => tryEntra(users[0])}
            className="w-full flex items-center justify-center gap-3 py-2.5 rounded font-semibold text-sm transition-transform active:scale-95"
            style={{ background: "#fff", color: "#111" }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16"><rect x="0" y="0" width="7.4" height="7.4" fill="#f25022"/><rect x="8.6" y="0" width="7.4" height="7.4" fill="#7fba00"/><rect x="0" y="8.6" width="7.4" height="7.4" fill="#00a4ef"/><rect x="8.6" y="8.6" width="7.4" height="7.4" fill="#ffb900"/></svg>
            Sign in with Microsoft Entra
          </button>
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px" style={{ background: C.line }} />
            <span className="text-[11px] uppercase tracking-widest" style={{ color: C.muted }}>or standalone</span>
            <div className="flex-1 h-px" style={{ background: C.line }} />
          </div>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@verabuilt.com"
            className="w-full mb-2 px-3 py-2.5 rounded text-sm outline-none" style={{ background: C.surface2, border: `1px solid ${C.line}`, color: C.text }} />
          <input value={pw} onChange={(e) => setPw(e.target.value)} type="password" placeholder="Password"
            className="w-full mb-3 px-3 py-2.5 rounded text-sm outline-none" style={{ background: C.surface2, border: `1px solid ${C.line}`, color: C.text }} />
          <Btn kind="primary" onClick={tryStandalone} className="w-full">Sign In</Btn>
          {err && <div className="mt-3 text-xs px-3 py-2 rounded" style={{ background: C.redDim, color: C.red }}>{err}</div>}
        </Card>

        <Card>
          <div className="text-[11px] uppercase tracking-widest mb-2 font-bold" style={{ color: C.muted }}>Demo — quick sign-in as</div>
          <div className="grid grid-cols-2 gap-2">
            {users.filter((u) => u.active).map((u) => (
              <button key={u.id} onClick={() => onLogin(u)}
                className="text-left px-3 py-2 rounded text-xs transition-colors"
                style={{ background: C.surface2, border: `1px solid ${C.line}`, color: C.text }}>
                <div className="font-semibold">{u.name}</div>
                <div style={{ color: C.amber }} className="uppercase tracking-wider text-[10px] font-bold">{u.role}</div>
              </button>
            ))}
          </div>
          <div className="mt-3 text-[11px]" style={{ color: C.muted }}>
            Auth mode: <span style={{ color: C.text }}>{settings.authMode}</span> · Approved domains: <span style={{ color: C.text }}>{settings.domains.join(", ")}</span>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ================= TRACKER (drill-down + timers + sign-off) ================= */

function Tracker({ user, tree, setTree, timers, setTimers, now, addTime, addSignOff, signOffs }) {
  const [path, setPath] = useState({ p: null, u: null, s: null, t: null });
  const isWorker = user.role === "worker";

  const setStatus = (stId, status) => {
    setTree((prev) => prev.map((p) => ({ ...p, units: p.units.map((u) => ({ ...u, stations: u.stations.map((s) => ({ ...s, tasks: s.tasks.map((t) => ({ ...t, subtasks: t.subtasks.map((st) => st.id === stId ? { ...st, status } : st) })) })) })) })));
  };

  const startTimer = (st) => setTimers((tm) => ({ ...tm, [st.id]: Date.now() }));
  const stopTimer = (st) => {
    const started = timers[st.id];
    if (!started) return;
    const hrs = Math.max((Date.now() - started) / 3600000, 0.01);
    addTime(st, hrs);
    setTimers((tm) => { const c = { ...tm }; delete c[st.id]; return c; });
    if (st.status === "not_started") setStatus(st.id, "in_progress");
  };

  const submit = (st) => {
    stopTimer(st);
    setStatus(st.id, "submitted");
    addSignOff({ subtaskId: st.id, step: "worker", by: user.id, action: "submitted", note: "" });
  };

  const crumbs = [
    { label: "Projects", onClick: () => setPath({ p: null, u: null, s: null, t: null }) },
    path.p && { label: path.p.name, onClick: () => setPath({ p: path.p, u: null, s: null, t: null }) },
    path.u && { label: path.u.code, onClick: () => setPath({ ...path, s: null, t: null }) },
    path.s && { label: path.s.name, onClick: () => setPath({ ...path, t: null }) },
    path.t && { label: path.t.name, onClick: () => {} },
  ].filter(Boolean);

  return (
    <div>
      <SectionTitle kicker="Production" title="Task Time Tracking" />
      <div className="flex flex-wrap items-center gap-1 mb-4 text-sm" style={{ color: C.muted }}>
        {crumbs.map((c, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <span style={{ color: C.line }}>›</span>}
            <button onClick={c.onClick} className="hover:underline" style={{ color: i === crumbs.length - 1 ? C.text : C.amber }}>{c.label}</button>
          </span>
        ))}
      </div>

      {!path.p && (
        <div className="grid gap-3">
          {tree.map((p) => (
            <Card key={p.id} className="cursor-pointer hover:border-amber-500 transition-colors" >
              <button className="w-full text-left" onClick={() => setPath({ p, u: null, s: null, t: null })}>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-lg font-semibold" style={{ fontFamily: "'Barlow Condensed',sans-serif" }}>{p.name}</div>
                    <div className="text-xs" style={{ color: C.muted }}>{p.client} · {p.units.length} home unit(s)</div>
                  </div>
                  <span style={{ color: C.amber }} className="text-xl">›</span>
                </div>
              </button>
            </Card>
          ))}
        </div>
      )}

      {path.p && !path.u && (
        <div className="grid gap-3 sm:grid-cols-2">
          {path.p.units.map((u) => (
            <Card key={u.id}>
              <button className="w-full text-left" onClick={() => setPath({ ...path, u })}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-xl font-bold" style={{ fontFamily: "'JetBrains Mono',monospace", color: C.amber }}>{u.code}</div>
                    <div className="text-sm">{u.model}</div>
                    <div className="text-xs mt-1 uppercase tracking-wider" style={{ color: C.muted }}>{u.status.replace("_", " ")}</div>
                  </div>
                  <span style={{ color: C.amber }} className="text-xl">›</span>
                </div>
              </button>
            </Card>
          ))}
        </div>
      )}

      {path.u && !path.s && (
        <div className="grid gap-3 sm:grid-cols-3">
          {path.u.stations.map((s) => {
            const all = s.tasks.flatMap((t) => t.subtasks);
            const done = all.filter((x) => x.status === "approved").length;
            return (
              <Card key={s.id}>
                <button className="w-full text-left" onClick={() => setPath({ ...path, s })}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATION_COLORS[s.name] || C.muted }} />
                    <span className="text-lg font-semibold" style={{ fontFamily: "'Barlow Condensed',sans-serif" }}>{s.name}</span>
                  </div>
                  <div className="text-xs mb-2" style={{ color: C.muted }}>{done}/{all.length} subtasks approved</div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: C.surface2 }}>
                    <div className="h-full rounded-full" style={{ width: `${all.length ? (done / all.length) * 100 : 0}%`, background: STATION_COLORS[s.name] || C.amber }} />
                  </div>
                </button>
              </Card>
            );
          })}
        </div>
      )}

      {path.s && (
        <div className="grid gap-4">
          {path.s.tasks.map((t) => (
            <Card key={t.id} pad={false} className="overflow-hidden">
              <div className="px-4 py-3 flex justify-between items-center" style={{ background: C.surface2, borderBottom: `1px solid ${C.line}` }}>
                <span className="font-semibold" style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 17 }}>{t.name}</span>
                <span className="text-xs" style={{ color: C.muted }}>{t.subtasks.length} subtask(s)</span>
              </div>
              <div>
                {t.subtasks.map((st) => {
                  const running = timers[st.id];
                  const mine = st.assignedTo === user.id;
                  const canWork = (!isWorker || mine) && ["not_started", "in_progress", "rejected"].includes(st.status);
                  const lastReject = signOffs.filter((s) => s.subtaskId === st.id && s.action === "rejected").slice(-1)[0];
                  return (
                    <div key={st.id} className="px-4 py-3 flex flex-wrap items-center gap-3" style={{ borderBottom: `1px solid ${C.line}` }}>
                      <div className="flex-1 min-w-[180px]">
                        <div className="text-sm font-medium">{st.name}</div>
                        <div className="text-[11px]" style={{ color: C.muted }}>Est {st.est}h · Assigned: {mine ? "You" : st.assignedTo}</div>
                        {st.status === "rejected" && lastReject?.note && (
                          <div className="text-[11px] mt-1" style={{ color: C.red }}>⚠ {lastReject.note}</div>
                        )}
                      </div>
                      <Chip status={running ? "in_progress" : st.status} />
                      <span className="text-sm font-semibold min-w-[86px] text-right" style={{ fontFamily: "'JetBrains Mono',monospace", color: running ? C.green : C.muted }}>
                        {running ? fmtClock((now - running) / 1000) : "—"}
                      </span>
                      {canWork && !running && <Btn small kind="primary" onClick={() => startTimer(st)}>▶ Start</Btn>}
                      {running && <Btn small kind="red" onClick={() => stopTimer(st)}>■ Stop</Btn>}
                      {canWork && (
                        <Btn small onClick={() => submit(st)} disabled={st.status === "not_started" && !running}>Submit for Sign-Off</Btn>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ================= APPROVALS (supervisor two-step sign-off) ================= */

function Approvals({ user, tree, setTree, signOffs, addSignOff, users }) {
  const [notes, setNotes] = useState({});
  const pending = [];
  for (const p of tree) for (const u of p.units) for (const s of u.stations) for (const t of s.tasks)
    for (const st of t.subtasks) if (st.status === "submitted") pending.push({ p, u, s, t, st });

  const setStatus = (stId, status) => {
    setTree((prev) => prev.map((p) => ({ ...p, units: p.units.map((u) => ({ ...u, stations: u.stations.map((s) => ({ ...s, tasks: s.tasks.map((t) => ({ ...t, subtasks: t.subtasks.map((x) => x.id === stId ? { ...x, status } : x) })) })) })) })));
  };

  const decide = (st, action) => {
    setStatus(st.id, action === "approved" ? "approved" : "rejected");
    addSignOff({ subtaskId: st.id, step: "supervisor", by: user.id, action, note: notes[st.id] || "" });
    setNotes((n) => ({ ...n, [st.id]: "" }));
  };

  const nameOf = (id) => users.find((u) => u.id === id)?.name || id;

  return (
    <div>
      <SectionTitle kicker="Quality Control" title="Sign-Off Queue" />
      {pending.length === 0 && (
        <Card><div className="text-sm py-6 text-center" style={{ color: C.muted }}>No subtasks awaiting supervisor sign-off. ✓</div></Card>
      )}
      <div className="grid gap-3">
        {pending.map(({ p, u, s, t, st }) => {
          const workerSubmit = signOffs.filter((x) => x.subtaskId === st.id && x.step === "worker").slice(-1)[0];
          return (
            <Card key={st.id}>
              <div className="flex flex-wrap justify-between gap-3">
                <div className="flex-1 min-w-[220px]">
                  <div className="text-[11px] uppercase tracking-wider mb-0.5" style={{ color: C.muted }}>
                    {p.name} › <span style={{ fontFamily: "'JetBrains Mono',monospace", color: C.amber }}>{u.code}</span> › {s.name} › {t.name}
                  </div>
                  <div className="font-semibold">{st.name}</div>
                  <div className="text-xs mt-1" style={{ color: C.muted }}>
                    Submitted by {nameOf(st.assignedTo)}{workerSubmit ? ` · ${workerSubmit.at || "just now"}` : ""}
                    {workerSubmit?.note && <span> · “{workerSubmit.note}”</span>}
                  </div>
                </div>
                <Chip status="submitted" />
              </div>
              <div className="mt-3 flex flex-wrap gap-2 items-center">
                <input
                  value={notes[st.id] || ""}
                  onChange={(e) => setNotes((n) => ({ ...n, [st.id]: e.target.value }))}
                  placeholder="QC note (required on reject)…"
                  className="flex-1 min-w-[200px] px-3 py-2 rounded text-sm outline-none"
                  style={{ background: C.surface2, border: `1px solid ${C.line}`, color: C.text }}
                />
                <Btn small kind="green" onClick={() => decide(st, "approved")}>✓ Approve</Btn>
                <Btn small kind="red" onClick={() => decide(st, "rejected")} disabled={!(notes[st.id] || "").trim()}>✕ Reject</Btn>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="mt-8">
        <SectionTitle kicker="Audit Trail" title="Recent Sign-Off Events" />
        <Card pad={false}>
          {signOffs.slice(-8).reverse().map((e) => {
            const f = findSubtask(tree, e.subtaskId);
            return (
              <div key={e.id} className="px-4 py-2.5 flex flex-wrap gap-2 items-center text-xs" style={{ borderBottom: `1px solid ${C.line}` }}>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", color: C.muted }}>{e.at || "now"}</span>
                <span className="font-semibold">{nameOf(e.by)}</span>
                <span style={{ color: e.action === "approved" ? C.green : e.action === "rejected" ? C.red : C.amber }} className="uppercase tracking-wider font-bold">{e.action}</span>
                <span style={{ color: C.muted }}>{f ? `${f.st.name} (${f.u.code} · ${f.s.name})` : e.subtaskId}</span>
                {e.note && <span style={{ color: C.muted }}>— “{e.note}”</span>}
              </div>
            );
          })}
        </Card>
      </div>
    </div>
  );
}

/* ================= DASHBOARD (fixed views per role + filters) ================= */

function Dashboard({ user, tree, timeEntries, users, visibility }) {
  const [fStation, setFStation] = useState("all");
  const [fWorker, setFWorker] = useState("all");
  const isWorker = user.role === "worker";

  const entries = timeEntries.filter((e) =>
    (isWorker ? e.userId === user.id : true) &&
    (fStation === "all" || e.station === fStation) &&
    (fWorker === "all" || e.userId === fWorker)
  );

  const allSubs = tree.flatMap((p) => p.units.flatMap((u) => u.stations.flatMap((s) => s.tasks.flatMap((t) => t.subtasks.map((st) => ({ ...st, station: s.name }))))));
  const scoped = isWorker ? allSubs.filter((s) => s.assignedTo === user.id) : allSubs;

  const totalHrs = entries.reduce((a, b) => a + b.hours, 0);
  const approved = scoped.filter((s) => s.status === "approved").length;
  const pendingQC = scoped.filter((s) => s.status === "submitted").length;
  const rejected = scoped.filter((s) => s.status === "rejected").length;

  const byStation = ["Flooring", "Plumbing", "Electrical"].map((st) => ({
    name: st,
    hours: +entries.filter((e) => e.station === st).reduce((a, b) => a + b.hours, 0).toFixed(1),
  }));

  const estVsActual = scoped.filter((s) => ["approved", "submitted", "in_progress", "rejected"].includes(s.status)).map((s) => {
    const actual = timeEntries.filter((e) => e.subtaskId === s.id).reduce((a, b) => a + b.hours, 0);
    return { name: s.name.length > 18 ? s.name.slice(0, 18) + "…" : s.name, est: s.est, actual: +actual.toFixed(1) };
  }).filter((r) => r.actual > 0);

  const kpis = [
    { label: "Logged Hours", value: totalHrs.toFixed(1), color: C.amber },
    { label: "Approved", value: approved, color: C.green },
    { label: "Awaiting QC", value: pendingQC, color: C.blue },
    { label: "Rejected", value: rejected, color: C.red },
  ];

  return (
    <div>
      <SectionTitle kicker={isWorker ? "My Performance" : "Plant Analytics"} title={isWorker ? "My Dashboard" : "Production Dashboard"} />

      <div className="flex flex-wrap gap-2 mb-4">
        <select value={fStation} onChange={(e) => setFStation(e.target.value)}
          className="px-3 py-2 rounded text-sm outline-none" style={{ background: C.surface2, border: `1px solid ${C.line}`, color: C.text }}>
          <option value="all">All Stations</option>
          {["Flooring", "Plumbing", "Electrical"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {!isWorker && (
          <select value={fWorker} onChange={(e) => setFWorker(e.target.value)}
            className="px-3 py-2 rounded text-sm outline-none" style={{ background: C.surface2, border: `1px solid ${C.line}`, color: C.text }}>
            <option value="all">All Workers</option>
            {users.filter((u) => u.role === "worker").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
        <span className="text-[11px] self-center px-2 py-1 rounded uppercase tracking-wider font-bold" style={{ background: C.surface2, color: C.muted }}>
          Fixed view · role: {user.role}
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {kpis.map((k) => (
          <Card key={k.label}>
            <div className="text-3xl font-bold" style={{ fontFamily: "'JetBrains Mono',monospace", color: k.color }}>{k.value}</div>
            <div className="text-[11px] uppercase tracking-widest mt-1" style={{ color: C.muted }}>{k.label}</div>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <div className="text-sm font-semibold mb-3" style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 16 }}>Hours by Station</div>
          <div style={{ height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={byStation}>
                <CartesianGrid stroke={C.line} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: C.muted, fontSize: 12 }} axisLine={{ stroke: C.line }} tickLine={false} />
                <YAxis tick={{ fill: C.muted, fontSize: 12 }} axisLine={{ stroke: C.line }} tickLine={false} />
                <Tooltip cursor={{ fill: "#ffffff08" }} contentStyle={{ background: C.surface2, border: `1px solid ${C.line}`, borderRadius: 8, color: C.text }} />
                <Bar dataKey="hours" radius={[4, 4, 0, 0]}>
                  {byStation.map((d) => <Cell key={d.name} fill={STATION_COLORS[d.name] || C.amber} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        {(!isWorker || visibility.worker.estVsActual) && (
          <Card>
            <div className="text-sm font-semibold mb-3" style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 16 }}>Estimated vs Actual Hours</div>
            <div style={{ height: 220 }}>
              <ResponsiveContainer>
                <BarChart data={estVsActual}>
                  <CartesianGrid stroke={C.line} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: C.muted, fontSize: 10 }} axisLine={{ stroke: C.line }} tickLine={false} />
                  <YAxis tick={{ fill: C.muted, fontSize: 12 }} axisLine={{ stroke: C.line }} tickLine={false} />
                  <Tooltip cursor={{ fill: "#ffffff08" }} contentStyle={{ background: C.surface2, border: `1px solid ${C.line}`, borderRadius: 8, color: C.text }} />
                  <Bar dataKey="est" fill={C.muted} radius={[4, 4, 0, 0]} name="Estimated" />
                  <Bar dataKey="actual" fill={C.amber} radius={[4, 4, 0, 0]} name="Actual" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

/* ================= CONTROL PANEL ================= */

function ControlPanel({ users, setUsers, settings, setSettings, visibility, setVisibility, timeEntries }) {
  const [tab, setTab] = useState("users");
  const [nu, setNu] = useState({ name: "", email: "", role: "worker" });
  const [nd, setNd] = useState("");
  const [showApi, setShowApi] = useState(false);

  const addUser = () => {
    if (!nu.name || !nu.email.includes("@")) return;
    setUsers((u) => [...u, { id: "u" + (u.length + 1) + Date.now().toString().slice(-4), ...nu, provider: settings.authMode === "entra_only" ? "entra" : "standalone", active: true }]);
    setNu({ name: "", email: "", role: "worker" });
  };

  const apiSample = JSON.stringify({
    data: timeEntries.slice(0, 3).map((e) => ({ id: e.id, subtask_id: e.subtaskId, user_id: e.userId, station: e.station, hours: e.hours })),
    meta: { page: 1, per_page: 100, total: timeEntries.length },
  }, null, 2);

  const tabs = [
    ["users", "Users & Roles"],
    ["auth", "Authentication"],
    ["visibility", "Field Visibility"],
    ["api", "REST API"],
  ];

  return (
    <div>
      <SectionTitle kicker="System Configuration" title="Control Panel" />
      <div className="flex flex-wrap gap-1 mb-5">
        {tabs.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className="px-3.5 py-2 rounded text-sm font-semibold"
            style={{ background: tab === id ? C.amber : C.surface, color: tab === id ? "#171006" : C.muted, border: `1px solid ${tab === id ? C.amber : C.line}` }}>
            {label}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <div className="grid gap-4">
          <Card pad={false}>
            <div className="grid grid-cols-[1fr_auto_auto_auto] sm:grid-cols-[1.5fr_2fr_auto_auto_auto] gap-2 px-4 py-2 text-[11px] uppercase tracking-wider font-bold" style={{ color: C.muted, borderBottom: `1px solid ${C.line}` }}>
              <span>Name</span><span className="hidden sm:block">Email</span><span>Role</span><span>Auth</span><span></span>
            </div>
            {users.map((u) => (
              <div key={u.id} className="grid grid-cols-[1fr_auto_auto_auto] sm:grid-cols-[1.5fr_2fr_auto_auto_auto] gap-2 px-4 py-2.5 items-center text-sm" style={{ borderBottom: `1px solid ${C.line}`, opacity: u.active ? 1 : 0.45 }}>
                <span className="font-medium truncate">{u.name}</span>
                <span className="hidden sm:block truncate" style={{ color: C.muted }}>{u.email}</span>
                <select value={u.role} onChange={(e) => setUsers((us) => us.map((x) => x.id === u.id ? { ...x, role: e.target.value } : x))}
                  className="px-2 py-1 rounded text-xs outline-none" style={{ background: C.surface2, border: `1px solid ${C.line}`, color: C.text }}>
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded" style={{ background: u.provider === "entra" ? C.blueDim : C.surface2, color: u.provider === "entra" ? C.blue : C.muted }}>{u.provider}</span>
                <Btn small kind={u.active ? "red" : "green"} onClick={() => setUsers((us) => us.map((x) => x.id === u.id ? { ...x, active: !x.active } : x))}>
                  {u.active ? "Deactivate" : "Restore"}
                </Btn>
              </div>
            ))}
          </Card>
          <Card>
            <div className="text-sm font-semibold mb-3">Add User</div>
            <div className="flex flex-wrap gap-2">
              <input value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} placeholder="Full name"
                className="flex-1 min-w-[140px] px-3 py-2 rounded text-sm outline-none" style={{ background: C.surface2, border: `1px solid ${C.line}`, color: C.text }} />
              <input value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} placeholder="email@verabuilt.com"
                className="flex-1 min-w-[180px] px-3 py-2 rounded text-sm outline-none" style={{ background: C.surface2, border: `1px solid ${C.line}`, color: C.text }} />
              <select value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value })}
                className="px-3 py-2 rounded text-sm outline-none" style={{ background: C.surface2, border: `1px solid ${C.line}`, color: C.text }}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <Btn kind="primary" onClick={addUser}>+ Add</Btn>
            </div>
            {nu.email && !settings.domains.some((d) => nu.email.toLowerCase().endsWith("@" + d)) && (
              <div className="mt-2 text-xs" style={{ color: C.amber }}>⚠ Email domain not in approved list — user will be blocked at login until domain is approved.</div>
            )}
          </Card>
        </div>
      )}

      {tab === "auth" && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <div className="text-sm font-semibold mb-3">Authentication Mode</div>
            {[
              ["both", "Entra SSO + Standalone", "Users may sign in with Microsoft Entra or email/password."],
              ["entra_only", "Force Microsoft Entra", "All users must authenticate through the client's Entra tenant."],
              ["standalone_only", "Standalone Only", "Email/password + TOTP only. Entra disabled."],
            ].map(([val, label, desc]) => (
              <button key={val} onClick={() => setSettings((s) => ({ ...s, authMode: val }))}
                className="w-full text-left px-3 py-3 rounded mb-2 transition-colors"
                style={{ background: settings.authMode === val ? C.amberDim : C.surface2, border: `1px solid ${settings.authMode === val ? C.amber : C.line}` }}>
                <div className="text-sm font-semibold flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full inline-block" style={{ background: settings.authMode === val ? C.amber : C.line }} />
                  {label}
                </div>
                <div className="text-xs mt-0.5 ml-5" style={{ color: C.muted }}>{desc}</div>
              </button>
            ))}
          </Card>
          <Card>
            <div className="text-sm font-semibold mb-1">Approved Sign-In Domains</div>
            <div className="text-xs mb-3" style={{ color: C.muted }}>Only these email domains can authenticate — applies to both Entra and standalone logins.</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {settings.domains.map((d) => (
                <span key={d} className="flex items-center gap-2 px-2.5 py-1 rounded text-xs font-semibold" style={{ background: C.surface2, border: `1px solid ${C.line}` }}>
                  @{d}
                  <button onClick={() => setSettings((s) => ({ ...s, domains: s.domains.filter((x) => x !== d) }))} style={{ color: C.red }}>✕</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={nd} onChange={(e) => setNd(e.target.value)} placeholder="newdomain.com"
                className="flex-1 px-3 py-2 rounded text-sm outline-none" style={{ background: C.surface2, border: `1px solid ${C.line}`, color: C.text }} />
              <Btn kind="primary" onClick={() => { if (nd.includes(".")) { setSettings((s) => ({ ...s, domains: [...s.domains, nd.toLowerCase().replace(/^@/, "")] })); setNd(""); } }}>+ Approve</Btn>
            </div>
          </Card>
        </div>
      )}

      {tab === "visibility" && (
        <Card>
          <div className="text-sm font-semibold mb-1">Field & Module Visibility — Worker Role</div>
          <div className="text-xs mb-4" style={{ color: C.muted }}>Control which fields and dashboard widgets the worker role can see. Supervisors and admins always see everything.</div>
          {[
            ["estVsActual", "Estimated vs Actual chart", "Show workers how their actual hours compare against estimates."],
            ["otherWorkers", "Other workers' assignments", "Show names on subtasks not assigned to the viewer."],
            ["laborCost", "Labor cost fields", "Hourly rate and cost roll-ups (hidden by default)."],
          ].map(([key, label, desc]) => (
            <label key={key} className="flex items-start gap-3 px-3 py-3 rounded mb-2 cursor-pointer" style={{ background: C.surface2, border: `1px solid ${C.line}` }}>
              <input type="checkbox" checked={!!visibility.worker[key]}
                onChange={(e) => setVisibility((v) => ({ ...v, worker: { ...v.worker, [key]: e.target.checked } }))}
                className="mt-0.5 accent-amber-500" />
              <span>
                <span className="text-sm font-medium block">{label}</span>
                <span className="text-xs" style={{ color: C.muted }}>{desc}</span>
              </span>
            </label>
          ))}
        </Card>
      )}

      {tab === "api" && (
        <Card>
          <div className="text-sm font-semibold mb-1">REST API — Power BI / External Integrations</div>
          <div className="text-xs mb-4" style={{ color: C.muted }}>Versioned read endpoints secured by scoped API keys. Connect Power BI via the Web connector or scheduled dataset refresh.</div>
          <div className="grid gap-2 mb-4">
            {[
              ["GET", "/api/v1/time-entries", "All time entries · filter: ?station=&user_id=&from=&to="],
              ["GET", "/api/v1/sign-offs", "Full two-step sign-off audit trail"],
              ["GET", "/api/v1/projects/:id/summary", "Roll-up: hours, completion %, est-vs-actual by station"],
              ["POST", "/api/v1/keys", "Issue scoped API key (admin only)"],
            ].map(([m, path, desc]) => (
              <div key={path} className="flex flex-wrap items-center gap-3 px-3 py-2 rounded" style={{ background: C.surface2, border: `1px solid ${C.line}` }}>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: m === "GET" ? C.greenDim : C.blueDim, color: m === "GET" ? C.green : C.blue, fontFamily: "'JetBrains Mono',monospace" }}>{m}</span>
                <span className="text-xs" style={{ fontFamily: "'JetBrains Mono',monospace", color: C.text }}>{path}</span>
                <span className="text-[11px]" style={{ color: C.muted }}>{desc}</span>
              </div>
            ))}
          </div>
          <Btn small onClick={() => setShowApi(!showApi)}>{showApi ? "Hide" : "Preview"} sample response</Btn>
          {showApi && (
            <pre className="mt-3 p-3 rounded text-[11px] overflow-x-auto" style={{ background: "#080b10", border: `1px solid ${C.line}`, color: C.cyan, fontFamily: "'JetBrains Mono',monospace" }}>
{apiSample}
            </pre>
          )}
        </Card>
      )}
    </div>
  );
}

/* ================= APP SHELL ================= */

export default function App() {
  const [users, setUsers] = useState(SEED_USERS);
  const [session, setSession] = useState(null);
  const [tree, setTree] = useState(SEED_TREE);
  const [timeEntries, setTimeEntries] = useState(SEED_TIME);
  const [signOffs, setSignOffs] = useState(SEED_SIGNOFFS);
  const [timers, setTimers] = useState({});
  const [now, setNow] = useState(Date.now());
  const [nav, setNav] = useState("tracker");
  const [settings, setSettings] = useState({ authMode: "both", domains: ["verabuilt.com", "fastbaysolutions.com"] });
  const [visibility, setVisibility] = useState({ worker: { estVsActual: true, otherWorkers: true, laborCost: false } });

  useEffect(() => {
    if (Object.keys(timers).length === 0) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [timers]);

  const addTime = (st, hours) => {
    const f = findSubtask(tree, st.id);
    setTimeEntries((te) => [...te, { id: "te" + Date.now(), subtaskId: st.id, userId: session.id, station: f?.s.name || "—", hours: +hours.toFixed(2), day: "Today" }]);
  };
  const addSignOff = (e) => setSignOffs((s) => [...s, { id: "so" + Date.now(), at: "Today " + new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }), ...e }]);

  if (!session) {
    return (<><style>{FONTS}</style><div style={{ fontFamily: "'IBM Plex Sans',sans-serif", color: C.text }}><LoginScreen users={users} settings={settings} onLogin={setSession} /></div></>);
  }

  const navItems = [
    ["tracker", "Tracker", ["admin", "supervisor", "worker"]],
    ["approvals", "Sign-Offs", ["admin", "supervisor"]],
    ["dashboard", "Dashboard", ["admin", "supervisor", "worker"]],
    ["control", "Control Panel", ["admin"]],
  ].filter(([, , roles]) => roles.includes(session.role));

  const pendingCount = tree.flatMap((p) => p.units.flatMap((u) => u.stations.flatMap((s) => s.tasks.flatMap((t) => t.subtasks)))).filter((s) => s.status === "submitted").length;

  return (
    <>
      <style>{FONTS}</style>
      <div className="min-h-screen" style={{ background: C.bg, color: C.text, fontFamily: "'IBM Plex Sans',sans-serif" }}>
        <header className="sticky top-0 z-20 px-4 sm:px-6 py-3 flex items-center gap-4" style={{ background: C.bg + "f2", backdropFilter: "blur(8px)", borderBottom: `1px solid ${C.line}` }}>
          <div className="text-xl font-bold leading-none" style={{ fontFamily: "'Barlow Condensed',sans-serif" }}>
            VERA<span style={{ color: C.amber }}>TRACK</span>
          </div>
          <nav className="flex gap-1 flex-1 overflow-x-auto">
            {navItems.map(([id, label]) => (
              <button key={id} onClick={() => setNav(id)}
                className="px-3 py-1.5 rounded text-sm font-semibold whitespace-nowrap relative"
                style={{ color: nav === id ? C.amber : C.muted, background: nav === id ? C.amberDim : "transparent" }}>
                {label}
                {id === "approvals" && pendingCount > 0 && (
                  <span className="ml-1.5 px-1.5 rounded-full text-[10px] font-bold" style={{ background: C.amber, color: "#171006" }}>{pendingCount}</span>
                )}
              </button>
            ))}
          </nav>
          <div className="text-right hidden sm:block">
            <div className="text-xs font-semibold">{session.name}</div>
            <div className="text-[10px] uppercase tracking-wider" style={{ color: C.amber }}>{session.role}</div>
          </div>
          <Btn small onClick={() => { setSession(null); setNav("tracker"); }}>Sign Out</Btn>
        </header>

        <main className="px-4 sm:px-6 py-6 max-w-6xl mx-auto">
          {nav === "tracker" && <Tracker user={session} tree={tree} setTree={setTree} timers={timers} setTimers={setTimers} now={now} addTime={addTime} addSignOff={addSignOff} signOffs={signOffs} />}
          {nav === "approvals" && <Approvals user={session} tree={tree} setTree={setTree} signOffs={signOffs} addSignOff={addSignOff} users={users} />}
          {nav === "dashboard" && <Dashboard user={session} tree={tree} timeEntries={timeEntries} users={users} visibility={visibility} />}
          {nav === "control" && session.role === "admin" && <ControlPanel users={users} setUsers={setUsers} settings={settings} setSettings={setSettings} visibility={visibility} setVisibility={setVisibility} timeEntries={timeEntries} />}
        </main>

        <footer className="px-6 py-4 text-center text-[11px]" style={{ color: C.muted, borderTop: `1px solid ${C.line}` }}>
          VeraTrack Demo · Fast Bay Solutions LLC · In-memory demo data — no backend connected
        </footer>
      </div>
    </>
  );
}
